// EmailTemplateView.js  (PREVIEW LEFT | SEND + BACK RIGHT)
import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getTrans as t } from "../../Localization/translations";
import EmailTemplateService from "../../services/email/emailServices";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

export default function EmailTemplateView() {
    const location = useLocation();
    const navigate = useNavigate();
    const queryParams = new URLSearchParams(location.search);
    const templateName = queryParams.get("id") || queryParams.get("name");

    const [loading, setLoading] = useState(true);
    const [subject, setSubject] = useState("");
    const [bodyHtml, setBodyHtml] = useState("");
    const [templateDesc, setTemplateDesc] = useState("");
    const [isOverride, setIsOverride] = useState(false);

    const [testEmail, setTestEmail] = useState("");
    const [invoiceNo, setInvoiceNo] = useState("");
    const [partyName, setPartyName] = useState("");
    const [ticketId, setTicketId] = useState("");

    // show hide
    const lowerName = templateName?.toLowerCase() || "";
    const isInvoiceTemplate =
        lowerName.includes("invoice") || lowerName.includes("due date");
    const isPasswordTemplate = templateName?.toLowerCase().includes("password");
    const isSupportTemplate = templateName?.toLowerCase().includes("support");


    /* --------------------------------------------------------------
       1. Load template → HTML ready for preview
       -------------------------------------------------------------- */
    useEffect(() => {
        if (!templateName) {
            setTemplateDesc("No template selected");
            setLoading(false);
            return;
        }

        setLoading(true);

        const plainToHtml = (plain) => {
            if (!plain) return "";
            return plain
                .replace(/&/g, "&amp;")
                .replace(/</g, "&lt;")
                .replace(/>/g, "&gt;")
                .replace(/\n\n+/g, "</p><p>")
                .replace(/\n/g, "<br>")
                .trim();
        };

        EmailTemplateService.getCompanyOverride(templateName)
            .then((res) => {
                const d = res.data;
                setSubject(d.subject || "");
                setBodyHtml(d.body_html || "");
                setTemplateDesc(d.base_template_name || templateName);
                setIsOverride(!!d.is_override);
            })
            .catch((err) => {
                if (err.response?.status === 404) {
                    // No override → use global template
                    EmailTemplateService.getTemplate(templateName)
                        .then((tmplRes) => {
                            const d = tmplRes.data;
                            setSubject(d.subject || "");
                            // Global: treat body as plain text and convert for preview
                            const clean = (d.body || "").replace(/<[^>]+>/g, "").trim();
                            setBodyHtml(plainToHtml(clean));
                            setTemplateDesc(d.name || templateName);
                            setIsOverride(false);
                        })
                        .catch(() => toast.error("Failed to load template"));
                } else {
                    toast.error("Failed to load override");
                }
            })
            .finally(() => setLoading(false));
    }, [templateName]);

    /* --------------------------------------------------------------
       2. Send Test Email (same style as editor)
       -------------------------------------------------------------- */
    const handleSend = async () => {
        if (!testEmail.trim()) {
            return toast.error("Enter a recipient email", { autoClose: 3000 });
        }

        if (!templateName) {
            return toast.error("No template selected", { autoClose: 3000 });
        }

        // Save override first (best-effort) – similar to EmailEditor
        try {
            await EmailTemplateService.updateCompanyOverride(templateName, {
                subject,
                body_html: bodyHtml, // already HTML for preview
            });
        } catch (err) {
            console.error(err);
            return toast.error("Failed to save template. Email not sent.", {
                autoClose: 5000,
            });
        }

        // Build send payload – IMPORTANT: match EmailEditor behaviour
        const sendPayload = {
            recipient: testEmail.trim(),
            invoice_number: invoiceNo || undefined,
            party_name: partyName || undefined,
            ticket_id: ticketId,

            // ❌ Do NOT send context here; backend builds it via build_auto_context
        };

        try {
            const res = await EmailTemplateService.sendTestEmail(templateName, sendPayload);

            toast.success(
                res?.data?.detail || `Email sent to ${testEmail.trim()}`,
                { autoClose: 4000 }
            );

            setTestEmail("");
        } catch (e) {
            console.error(e);
            const msg =
                e?.response?.data?.detail ||
                e?.response?.data?.error ||
                "Send failed";
            toast.error(msg, { autoClose: 5000 });
        }

    };

    /* --------------------------------------------------------------
       3. Full HTML preview
       -------------------------------------------------------------- */
    const renderPreview = () => {
        const fullHtml = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>${subject}</title>
  <style>
    body {
      font-family: Arial, Helvetica, sans-serif;
      margin: 0;
      background: #245c7f;
    }
    .email {
      max-width: 600px;
      margin: 30px auto;
      background: #fff;
      border-radius: 12px;
      overflow: hidden;
      box-shadow: 0 10px 30px rgba(0,0,0,0.15);
    }
    .header {
      background: #2c3e50;
      color: #ffffff !important;
      padding: 12px 20px;
      text-align: center;
      font-size: 24px;
      font-weight: 600;
      letter-spacing: 0.5px;
      text-shadow: 0 1px 2px rgba(0,0,0,0.3);
    }
    .header h2 {
      margin: 0;
      color: #ffffff !important;
    }
    .body {
      padding: 40px 35px;
      line-height: 1.8;
      color: #333;
      white-space: pre-line;
    }
    .body p {
      margin: 0 0 1.2em;
    }
    .footer {
      background: #ecf0f1;
      padding: 20px;
      text-align: center;
      font-size: 13px;
      color: #7f8c8d;
    }
  </style>
</head>
<body>
  <div class="email">
    <div class="header">
      <h2>${subject || "Email Preview"}</h2>
    </div>
    <div class="body">${bodyHtml || ""}</div>
    <div class="footer">
      <em>Preview only • Variables will be replaced when sent</em>
    </div>
  </div>
</body>
</html>`;

        return { __html: fullHtml };
    };

    /* --------------------------------------------------------------
       4. UI – LEFT: Preview | RIGHT: Send + Back
       -------------------------------------------------------------- */
    if (loading) {
        return (
            <div className="page-wrapper">
                <div className="content container-fluid pt-3 text-center">
                    <p>{t("Loading…")}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="container-fluid bg-light min-vh-100 p-4">
            <main>
                <div className="row">
                    <div className="col-12">
                        <div className="card">
                            <div className="card-header d-flex justify-content-between align-items-center">
                                <h5 className="mb-0">{templateName}</h5>
                                {/* {templateDesc && (
                                    <small className="text-muted">
                                        {isOverride ? t("Company Override") : t("Global Template")}{" "}
                                        • {templateDesc}
                                    </small>
                                )} */}
                            </div>

                            <div className="card-body p-0">
                                <div className="row g-0" style={{ minHeight: "70vh" }}>
                                    {/* LEFT: Full Preview */}
                                    <div className="col-lg-7 border-end">
                                        <div dangerouslySetInnerHTML={renderPreview()} />
                                    </div>

                                    {/* RIGHT: Send & Back */}
                                    <div className="col-lg-5 d-flex align-items-start justify-content-center p-4">
                                        <div className="w-100" style={{ maxWidth: "360px" }}>
                                            <h6 className="mb-3 col-form-label">{t("Send Email")}</h6>

                                            <div className="mb-3">
                                                <label className="col-form-label">{t("To")}</label>
                                                <input
                                                    type="email"
                                                    className="form-control"
                                                    placeholder="recipient@example.com"
                                                    value={testEmail}
                                                    onChange={(e) => setTestEmail(e.target.value)}
                                                />
                                            </div>

                                            {isInvoiceTemplate && (
                                                <div className="mb-3">
                                                    <label className="col-form-label">Invoice No</label>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        placeholder="invoice number"
                                                        value={invoiceNo}
                                                        onChange={(e) => setInvoiceNo(e.target.value)}
                                                    />
                                                </div>
                                            )}

                                            {isPasswordTemplate && (
                                                <div className="mb-3">
                                                    <label className="col-form-label">Party Name</label>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        placeholder="party name"
                                                        value={partyName}
                                                        onChange={(e) => setPartyName(e.target.value)}
                                                    />
                                                </div>
                                            )}
                                            {isSupportTemplate && (
                                                <div className="mb-3">
                                                    <label className="col-form-label">Ticket No</label>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        placeholder="ticket number"
                                                        value={ticketId}
                                                        onChange={(e) => setTicketId(e.target.value)}
                                                    />
                                                </div>
                                            )}

                                            <div className="d-flex gap-2">
                                                <button
                                                    className="btn btn-primary flex-fill"
                                                    onClick={handleSend}
                                                    disabled={!testEmail.trim()}
                                                >
                                                    {t("Send")}
                                                </button>
                                                <button
                                                    className="btn btn-outline-secondary flex-fill"
                                                    onClick={() => navigate(-1)}
                                                >
                                                    {t("Back")}
                                                </button>
                                            </div>

                                    
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <ToastContainer />
            </main>
        </div>
    );
}
